import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Product } from 'src/app/order/models/product.model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * A form's cost field as the backend reads it: blank means "not priced" (null), and
 * 0 is a real price. The backend treats null and 0 as different, so they must not
 * be folded together with `+cost || 0`.
 */
export function costOrNull(value: any): number | null {
  if(value === undefined || value === null || value === '') {
    return null;
  }

  const cost = Number(value);
  return Number.isFinite(cost) ? cost : null;
}

@Injectable({
  providedIn: 'root'
})
export class ProductService {

 /**
  * Endpoint for http calls
  */
 private _endpoint: string = environment.api.endpoint;


 constructor(private httpClient: HttpClient) { }


 getAllActiveProducts(): Observable<Product[]> {
  return this.httpClient.get(this._endpoint + 'product?filter=active||eq||1').pipe(map((data) => {
    return <Product[]>data;
  }))
 }
 /**
  * Get a prodict from the backend by id
  *
  * @param id number id of product to get
  *
  * @returns Observable<Product>
  */
 getProductById(id: number): Observable<Product> {

   if(id <= 0 || id === null || id === undefined) {
     throw new Error('ProductService: Id must be a valid number');
   }

  return this.httpClient.get(this._endpoint + 'product/' + id).pipe(map((value: any) => {
     return <Product>value;
  }));
 }


 /**
  * creates a product in the backend based on product object.
  * @param Product product object.
  *
  * @returns Observable<Product>
  */
 createProduct(product: Product): Observable<Product> {


   if(!product) {
     throw new Error('product service: you must supply a product object for saving');
   }

   delete product.createdAt;
   delete product.updatedAt;

   if(product.id > 0) {
     throw new Error('product service: Cannot create a existing object');
   }

   delete product.id;
   return this.httpClient.post(this._endpoint + 'product',
   JSON.stringify(product),
   {headers: {'Content-Type': 'application/json'}}).pipe(map((data) => {
     return <Product>data;
   }));
 }


 /**
  * updates a product in the backend based on product object.
  * @param product product object.
  *
  * @returns Observable<Product>
  */
 updateProduct(product: Product): Observable<Product> {


   if(!product) {
     throw new Error('product service: you must supply a product object for saving');
   }

   delete product.createdAt;
   delete product.updatedAt;


   if(!product.id || product.id === -1) {
     throw new Error('product service: Cannot update an record without id');
   }

   return this.httpClient.put(this._endpoint + 'product/' + product.id,
   JSON.stringify(product),
   {headers: {'Content-Type': 'application/json'}}).pipe(map((data) => {
     return <Product>data;
   }));
 }


 /**
  * Changes a product's buying cost.
  *
  * Cost has its own route because every change to it has to leave an entry in the
  * product cost history, and the ordinary create/update routes refuse a cost they
  * would have to move rather than write one silently. So a form cannot save cost the
  * way it saves every other field - it has to come through here, or it does not
  * persist at all.
  *
  * @param id number id of the product to reprice
  * @param cost number the new buying cost. Zero is valid: 3PL stock is not ours to value.
  *             null clears it back to "not priced".
  * @param notes string why it changed, recorded on the audit entry
  */
 setProductCost(id: number, cost: number | null, notes: string = ''): Observable<any> {

   if(!id || id <= 0) {
     throw new Error('product service: cannot set a cost without a valid product id');
   }

   return this.httpClient.patch(this._endpoint + 'product/cost/' + id,
   JSON.stringify({cost: cost, notes: notes}),
   {headers: {'Content-Type': 'application/json'}});
 }
}
